import { useTranslation } from "react-i18next";

import { useAreaReservationCount } from "@lib/logic";
import type { AreaWithSensorInfo } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { Icon } from "@atoms";
import { CapacityIndicator } from "@molecules";
import { cn } from "@utils";

import { HaNContext } from "../../../shell/HaNContext";

// How many of a bookable area's places are left today. The count loads again with each refresh bucket, and keeps the
// previous one on screen meanwhile.
export const AreaBookingCount = ({ area }: { area: AreaWithSensorInfo }) => {
  const { t } = useTranslation();
  const { bucket } = useRequiredContext(HaNContext);
  const count = useAreaReservationCount({ areaId: area.id, bucket });

  const booked = count ?? 0;
  const isFullyBooked = booked >= area.capacity;

  return (
    <div className="flex items-center gap-4">
      <Icon
        icon="calendar"
        size="24px"
        color="currentColor"
        className="flex-none text-mapiq-black-400"
      />
      <div className="flex flex-col">
        <h4 className="text-[15px] font-semibold leading-5">
          {t(area.hasBookableDesks ? "screen:DeskDetail" : "hereAndNow:AreaBooking")}
        </h4>
        {/* Until the first count is in it keeps its space hidden, instead of showing every place as free */}
        <div className={cn("flex items-center gap-2", { invisible: count === null })}>
          <CapacityIndicator
            capacity={area.capacity}
            occupation={booked}
            displayDescription={false}
          />
          <p className={cn("text-mapiq-black-500", { "text-functional-red": isFullyBooked })}>
            {isFullyBooked
              ? t("translation:FullyBooked")
              : `${area.capacity - booked}/${area.capacity} ${t("workplace:WorkspaceGroupAvailable")}`}
          </p>
        </div>
      </div>
    </div>
  );
};
